// frontend/src/dashboardv2/hooks/useDashboardData.js
//
// Fetches dashboardv2 data in two stages:
//   1) hero-critical data first (profile + saved searches)
//   2) non-critical sections after hero can render
// Returns a normalized object with data + loading/error state.
//
// This intentionally does NOT include the hero "newest match" — that has its
// own hook (useNewestMatch) because it has fallback logic that depends on
// having the saved searches list first.

import { useEffect, useState, useCallback, useRef } from 'react';
import logger from '../../utils/logger';
import {
  fetchSavedSearches,
  fetchProfileViews,
  fetchFavorites,
  fetchShortlist,
  fetchExclusions,
  fetchNotes,
  fetchConversations,
  fetchTheirFavorites,
  fetchTheirShortlist,
  fetchIncomingPiiRequests,
  fetchActivePolls,
  fetchCurrentUserProfile,
  fetchDashboardSummary,
  fetchSearchCriteriaBreakdown,
} from '../api';

const getCurrentUsername = () => localStorage.getItem('username');
const DEFERRED_POLLS_DELAY_MS = 1200;

// Short-lived sessionStorage snapshot so revisiting /dashboardv2 paints
// instantly with the last-known data while a background refetch revalidates.
// Staleness is bounded: refetch always runs on mount and replaces it in ~1 RTT.
const SNAPSHOT_TTL_MS = 5 * 60 * 1000;
const SNAPSHOT_FIELDS = [
  'userProfile', 'savedSearches', 'profileViews', 'favorites', 'shortlist',
  'exclusions', 'notes', 'conversations', 'theirFavorites', 'theirShortlist',
  'incomingPiiRequests',
];

const loadDashboardSnapshot = () => {
  try {
    const raw = sessionStorage.getItem(`dv2Snapshot:${getCurrentUsername()}`);
    if (!raw) return null;
    const { data, expiresAt } = JSON.parse(raw);
    if (!expiresAt || Date.now() > expiresAt || !data?.userProfile) return null;
    return data;
  } catch {
    return null;
  }
};

const persistDashboardSnapshot = (data) => {
  try {
    const snapshot = {};
    SNAPSHOT_FIELDS.forEach((field) => {
      if (data[field] !== undefined) snapshot[field] = data[field];
    });
    sessionStorage.setItem(
      `dv2Snapshot:${getCurrentUsername()}`,
      JSON.stringify({ data: snapshot, expiresAt: Date.now() + SNAPSHOT_TTL_MS })
    );
  } catch {
    // sessionStorage unavailable/full — snapshot is best-effort, safe to skip
  }
};
const getFavoriteUsername = (favorite) => favorite?.favoriteUsername || favorite?.targetUsername || favorite?.username || null;

const initialData = {
  userProfile: null,
  savedSearches: [],
  profileViews: { viewers: [], totalViews: 0, uniqueViewers: 0 },
  favorites: [],
  shortlist: [],
  exclusions: [],
  notes: [],
  conversations: [],
  theirFavorites: [],
  theirShortlist: [],
  incomingPiiRequests: [],
  activePolls: [],
  searchCriteriaBreakdown: null,
};

export function useDashboardData() {
  // Read the snapshot once per mount — it hydrates the initial state below.
  const snapshotRef = useRef();
  if (snapshotRef.current === undefined) {
    snapshotRef.current = loadDashboardSnapshot();
  }
  const hasSnapshot = !!snapshotRef.current;

  const [data, setData] = useState(
    hasSnapshot ? { ...initialData, ...snapshotRef.current } : initialData
  );
  // With a snapshot, content paints immediately — skip the loading skeletons.
  const [loading, setLoading] = useState(!hasSnapshot);
  const [criticalLoading, setCriticalLoading] = useState(!hasSnapshot);
  const [error, setError] = useState(null);
  const deferredPollsTimerRef = useRef(null);
  const activePollsRequestRef = useRef(null);
  const userProfileRequestRef = useRef(null);
  const exclusionsRequestRef = useRef(null);
  const favoritesRequestRef = useRef(null);
  const conversationsRequestRef = useRef(null);

  const refreshUserProfile = useCallback(async () => {
    if (userProfileRequestRef.current) {
      return userProfileRequestRef.current;
    }

    const request = (async () => {
      const userProfile = await fetchCurrentUserProfile();
      setData((prev) => ({
        ...prev,
        userProfile,
      }));
      return userProfile;
    })();

    userProfileRequestRef.current = request;

    try {
      return await request;
    } finally {
      if (userProfileRequestRef.current === request) {
        userProfileRequestRef.current = null;
      }
    }
  }, []);

  const refreshConversations = useCallback(async () => {
    if (conversationsRequestRef.current) {
      return conversationsRequestRef.current;
    }

    const request = (async () => {
      const conversations = await fetchConversations();
      setData((prev) => ({
        ...prev,
        conversations,
      }));
      return conversations;
    })();

    conversationsRequestRef.current = request;

    try {
      return await request;
    } finally {
      if (conversationsRequestRef.current === request) {
        conversationsRequestRef.current = null;
      }
    }
  }, []);

  const refreshFavorites = useCallback(async () => {
    if (favoritesRequestRef.current) {
      return favoritesRequestRef.current;
    }

    const request = (async () => {
      const favorites = await fetchFavorites();
      setData((prev) => ({
        ...prev,
        favorites,
      }));
      return favorites;
    })();

    favoritesRequestRef.current = request;

    try {
      return await request;
    } finally {
      if (favoritesRequestRef.current === request) {
        favoritesRequestRef.current = null;
      }
    }
  }, []);

  const setFavoriteOptimistic = useCallback((targetUsername, shouldBeFavorited) => {
    if (!targetUsername) return;

    setData((prev) => {
      const currentFavorites = Array.isArray(prev.favorites) ? prev.favorites : [];
      const exists = currentFavorites.some((favorite) => getFavoriteUsername(favorite) === targetUsername);

      if (shouldBeFavorited) {
        if (exists) return prev;
        return {
          ...prev,
          favorites: [
            ...currentFavorites,
            {
              favoriteUsername: targetUsername,
              targetUsername,
              username: targetUsername,
            },
          ],
        };
      }

      if (!exists) return prev;
      return {
        ...prev,
        favorites: currentFavorites.filter((favorite) => getFavoriteUsername(favorite) !== targetUsername),
      };
    });
  }, []);

  const refreshExclusions = useCallback(async () => {
    if (exclusionsRequestRef.current) {
      return exclusionsRequestRef.current;
    }

    const request = (async () => {
      const exclusions = await fetchExclusions();
      setData((prev) => ({
        ...prev,
        exclusions,
      }));
      return exclusions;
    })();

    exclusionsRequestRef.current = request;

    try {
      return await request;
    } finally {
      if (exclusionsRequestRef.current === request) {
        exclusionsRequestRef.current = null;
      }
    }
  }, []);

  const refreshActivePolls = useCallback(async () => {
    if (activePollsRequestRef.current) {
      return activePollsRequestRef.current;
    }

    const request = (async () => {
      const activePolls = await fetchActivePolls();
      setData((prev) => ({
        ...prev,
        activePolls,
      }));
      return activePolls;
    })();

    activePollsRequestRef.current = request;

    try {
      return await request;
    } finally {
      if (activePollsRequestRef.current === request) {
        activePollsRequestRef.current = null;
      }
    }
  }, []);

  const refetch = useCallback(async ({ deferPolls = true, background = false } = {}) => {
    if (deferredPollsTimerRef.current) {
      clearTimeout(deferredPollsTimerRef.current);
      deferredPollsTimerRef.current = null;
    }

    // Background revalidations (mount-time refresh over a snapshot) must not
    // flip the loading skeletons back on over already-painted content.
    if (!background) {
      setLoading(true);
      setCriticalLoading(true);
    }
    setError(null);

    try {
      // Stage 1: hero-critical data only
      // Unblocks newest-match hero as soon as profile + saved searches are ready.
      const [
        userProfile,
        savedSearches,
      ] = await Promise.all([
        fetchCurrentUserProfile(),
        fetchSavedSearches(),
      ]);

      setData((prev) => ({
        ...prev,
        userProfile,
        savedSearches,
        searchCriteriaBreakdown: null,
      }));

      // Hero can render now while the rest of dashboard data continues loading.
      setCriticalLoading(false);

      // Stage 2: non-critical dashboard sections.
      // Prefer the aggregated /dashboard/summary endpoint (one request); fall
      // back to the nine parallel calls on backends that don't have it yet.
      const summary = await fetchDashboardSummary();

      let profileViews;
      let favorites;
      let shortlist;
      let exclusions;
      let notes;
      let conversations;
      let theirFavorites;
      let theirShortlist;
      let incomingPiiRequests;

      if (summary) {
        const rawViews = summary.profileViews || {};
        const views = Array.isArray(rawViews.views) ? rawViews.views : [];
        const viewers = Array.isArray(rawViews.viewers)
          ? rawViews.viewers
          : views.map((v) => v?.viewerProfile).filter(Boolean);
        profileViews = {
          ...rawViews,
          views,
          viewers,
          uniqueViewers: Number.isFinite(rawViews.uniqueViewers) ? rawViews.uniqueViewers : viewers.length,
          totalViews: Number.isFinite(rawViews.totalViews) ? rawViews.totalViews : views.length,
        };
        favorites = summary.favorites || [];
        shortlist = summary.shortlist || [];
        exclusions = summary.exclusions || [];
        notes = summary.notes || [];
        conversations = summary.conversations || [];
        theirFavorites = summary.theirFavorites || [];
        theirShortlist = summary.theirShortlist || [];
        incomingPiiRequests = summary.incomingPiiRequests || [];
      } else {
        [
          profileViews,
          favorites,
          shortlist,
          exclusions,
          notes,
          conversations,
          theirFavorites,
          theirShortlist,
          incomingPiiRequests,
        ] = await Promise.all([
          fetchProfileViews(),
          fetchFavorites(),
          fetchShortlist(),
          fetchExclusions(),
          fetchNotes(),
          fetchConversations(),
          fetchTheirFavorites(),
          fetchTheirShortlist(),
          fetchIncomingPiiRequests(getCurrentUsername()),
        ]);
      }

      setData((prev) => ({
        ...prev,
        profileViews,
        favorites,
        shortlist,
        exclusions,
        notes,
        conversations,
        theirFavorites,
        theirShortlist,
        incomingPiiRequests,
      }));

      persistDashboardSnapshot({
        userProfile,
        savedSearches,
        profileViews,
        favorites,
        shortlist,
        exclusions,
        notes,
        conversations,
        theirFavorites,
        theirShortlist,
        incomingPiiRequests,
      });

      if (deferPolls) {
        deferredPollsTimerRef.current = setTimeout(() => {
          deferredPollsTimerRef.current = null;
          refreshActivePolls();
        }, DEFERRED_POLLS_DELAY_MS);
      } else {
        await refreshActivePolls();
      }
    } catch (err) {
      logger.error('useDashboardData refetch failed:', err);
      setError(err);
    } finally {
      setLoading(false);
      setCriticalLoading(false);
    }
  }, [refreshActivePolls]);

  const fetchBreakdown = useCallback(async (criteria) => {
    const username = getCurrentUsername();
    if (!criteria || !username) return;

    try {
      const breakdown = await fetchSearchCriteriaBreakdown(username, criteria);
      setData((prev) => ({
        ...prev,
        searchCriteriaBreakdown: breakdown,
      }));
    } catch (err) {
      logger.warn('Failed to fetch search criteria breakdown (non-critical):', err);
    }
  }, []);

  useEffect(() => {
    // Snapshot hydrated → this mount refetch is a silent background revalidate.
    refetch({ deferPolls: true, background: hasSnapshot });
  }, [refetch, hasSnapshot]);

  useEffect(() => () => {
    if (deferredPollsTimerRef.current) {
      clearTimeout(deferredPollsTimerRef.current);
      deferredPollsTimerRef.current = null;
    }
  }, []);

  return {
    data,
    loading,
    criticalLoading,
    error,
    refetch,
    fetchBreakdown,
    refreshActivePolls,
    refreshUserProfile,
    refreshExclusions,
    refreshFavorites,
    refreshConversations,
    setFavoriteOptimistic,
  };
}
